// frontend/src/pages/admin/ContractForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, Select, Row, Col, Popconfirm, Table, DatePicker, Divider, Checkbox, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useNotificationApi } from '../../context/NotificationContext';
import ContractSlaManagementModal from './ContractSlaManagementModal';

dayjs.extend(isSameOrAfter);

const { Option } = Select;

const ContractForm = ({ visible, onCancel, onSuccess, contract }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [allMainContractorOrgs, setAllMainContractorOrgs] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [productToAssign, setProductToAssign] = useState(null);
  const [assigningLoading, setAssigningLoading] = useState(false);
  const [isSlaModalVisible, setIsSlaModalVisible] = useState(false);

  // 🆕 Client Computing Portal — EUC user assignment state
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userToAssign, setUserToAssign] = useState(null);
  const [assignEUCEnabled, setAssignEUCEnabled] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const notificationApi = useNotificationApi();

  // Helper functions for consistent notifications
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: { width: 400, zIndex: 10000 },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({ title, content, centered: true, zIndex: 99999 });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: { width: 400, zIndex: 10000 },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;
    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: { width: 400, zIndex: 10000 },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // --- Fetching Data for Dropdowns and Assigned Products ---

  const fetchAllOrganizations = useCallback(async () => {
    const loadingKey = 'fetchOrgsDropdownLoading';
    const hideLoading = showLoading(loadingKey, 'Loading organizations...');
    try {
      const response = await api.get('/api/organizations', { params: { status: 'active', includeDeleted: false, type: 'customer' } });
      setAllOrganizations(response.data.data || []);
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching all organizations:', error.response?.data || error.message);
      showError('Failed to Load Organizations', error.response?.data?.message || 'Failed to load organizations.', 'fetchOrgsDropdownError');
    }
  }, [showLoading, showError]);

  const fetchMainContractorOrgs = useCallback(async () => {
    const loadingKey = 'fetchMainContractorDropdownLoading';
    const hideLoading = showLoading(loadingKey, 'Loading main contractors...');
    try {
      const response = await api.get('/api/organizations', {
        params: { status: 'active', includeDeleted: false }
      });
      const filteredOrgs = (response.data.data || []).filter(
        org => org.type === 'internal' || org.type === 'partner'
      );
      console.log('Filtered main contractor orgs:', filteredOrgs);
      setAllMainContractorOrgs(filteredOrgs);
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching main contractor organizations:', error.response?.data || error.message);
      showError('Failed to Load Main Contractors', error.response?.data?.message || 'Failed to load main contractors.', 'fetchMainContractorDropdownError');
    }
  }, [showLoading, showError]);

  const fetchAllProductsForAssignment = useCallback(async () => {
    const loadingKey = 'fetchProductsDropdownLoading';
    const hideLoading = showLoading(loadingKey, 'Loading products...');
    try {
      const response = await api.get('/api/products', { params: { status: 'active', includeDeleted: false } });
      setAllProducts(response.data.data || []);
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error fetching all products for assignment:', error.response?.data || error.message);
      showError('Failed to Load Products', error.response?.data?.message || 'Failed to load products for assignment.', 'fetchProductsDropdownError');
    }
  }, [showLoading, showError]);

  const refetchAssignedProducts = useCallback(async () => {
    if (!contract || !contract.id) {
      console.warn('ContractForm: refetchAssignedProducts called without a valid contract ID.');
      setAssignedProducts([]);
      return;
    }
    setAssigningLoading(true);
    const loadingKey = 'refetchAssignedProductsLoading';
    const hideLoading = showLoading(loadingKey, 'Refreshing assigned products...');
    try {
      const response = await api.get(`/api/contracts/${contract.id}`);
      setAssignedProducts(response.data.data.products || []);
      console.info('ContractForm: Refetched assigned products:', response.data.data.products);
      hideLoading();
    } catch (error) {
      hideLoading();
      console.error('Error refetching assigned products:', error.response?.data || error.message);
      showError('Failed to Refresh Products', error.response?.data?.message || 'Failed to refresh assigned products.', 'refetchAssignedProductsError');
    } finally {
      setAssigningLoading(false);
    }
  }, [contract, showLoading, showError]);

  // 🆕 Fetch all active users (for contract-user assignment dropdown)
  const fetchAvailableUsers = useCallback(async () => {
    try {
      const response = await api.get('/api/contracts/available-users');
      setAvailableUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching available users for contract assignment:', error.response?.data || error.message);
    }
  }, []);

  // 🆕 Fetch users assigned to this contract (contract_users)
  const refetchAssignedUsers = useCallback(async () => {
    if (!contract || !contract.id) {
      setAssignedUsers([]);
      setAssignEUCEnabled(false);
      return;
    }
    setUsersLoading(true);
    try {
      const response = await api.get(`/api/contracts/${contract.id}/users`);
      const users = response.data.data || [];
      setAssignedUsers(users);
      if (users.length > 0) {
        setAssignEUCEnabled(true);
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching assigned users:', error.response?.data || error.message);
      }
      setAssignedUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [contract]);

  // --- Effect Hook for Form Initialization ---

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setAssignedProducts([]);
      setProductToAssign(null);
      setAllOrganizations([]);
      setAllMainContractorOrgs([]);
      setAllProducts([]);

      setAssignedUsers([]);
      setAvailableUsers([]);
      setUserToAssign(null);
      setAssignEUCEnabled(false);
      setUsersLoading(false);
      return;
    }

    fetchAllOrganizations();
    fetchMainContractorOrgs();
    fetchAllProductsForAssignment();
    fetchAvailableUsers();

    if (contract) {
      console.info('ContractForm: Initializing form for editing contract:', contract);
      form.setFieldsValue({
        organizationId: contract.organizationId,
        mainContractorOrganizationId: contract.mainContractorOrganizationId,
        contractName: contract.contractName,
        reportTitle: contract.reportTitle,
        startDate: contract.startDate ? dayjs(contract.startDate) : null,
        endDate: contract.endDate ? dayjs(contract.endDate) : null,
        ladPenaltyRate: contract.ladPenaltyRate,
        maintenancePeriodUnit: contract.maintenancePeriodUnit,
        agreedResponseTimeValue: contract.agreedResponseTimeValue,
        agreedResponseTimeUnit: contract.agreedResponseTimeUnit,
        agreedResolutionTimeValue: contract.agreedResolutionTimeValue,
        agreedResolutionTimeUnit: contract.agreedResolutionTimeUnit,
        isActive: Boolean(contract.isActive),
      });
      if (contract.products) {
        setAssignedProducts(contract.products);
      } else {
        refetchAssignedProducts();
      }

      refetchAssignedUsers();
    } else {
      console.info('ContractForm: Resetting form fields for new contract.');
      form.resetFields();
      setAssignedProducts([]);
      setProductToAssign(null);
      setAssignedUsers([]);
      setAssignEUCEnabled(false);
      form.setFieldsValue({
        ladPenaltyRate: 0,
        maintenancePeriodUnit: 'Yearly',
        agreedResponseTimeValue: 1,
        agreedResponseTimeUnit: 'hour',
        agreedResolutionTimeValue: 4,
        agreedResolutionTimeUnit: 'hour',
        isActive: true,
        mainContractorOrganizationId: 1,
      });
    }
  }, [visible, contract, form, fetchAllOrganizations, fetchMainContractorOrgs, fetchAllProductsForAssignment, refetchAssignedProducts, fetchAvailableUsers, refetchAssignedUsers]);

  // --- Form Submission Handler ---

  const handleSubmit = async () => {
    let hideLoading;
    try {
      setLoading(true);
      const loadingKey = 'contractSubmitLoading';
      hideLoading = showLoading(loadingKey, contract ? 'Updating contract...' : 'Creating contract...');

      const values = await form.validateFields();
      console.info('ContractForm: Form values validated:', values);

      if (contract && assignEUCEnabled && assignedUsers.length === 0) {
        if (hideLoading && typeof hideLoading === 'function') hideLoading();
        setLoading(false);
        showError(
          'EUC Users Required',
          'Please assign at least one user, or uncheck the EUC checkbox.',
          'eucUsersValidation'
        );
        return;
      }

      const payload = {
        organizationId: Number(values.organizationId),
        mainContractorOrganizationId: Number(values.mainContractorOrganizationId),
        contractName: values.contractName,
        reportTitle: values.reportTitle,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : null,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        ladPenaltyRate: Number(values.ladPenaltyRate),
        maintenancePeriodUnit: values.maintenancePeriodUnit,
        agreedResponseTimeValue: Number(values.agreedResponseTimeValue),
        agreedResponseTimeUnit: values.agreedResponseTimeUnit,
        agreedResolutionTimeValue: Number(values.agreedResolutionTimeValue),
        agreedResolutionTimeUnit: values.agreedResolutionTimeUnit,
        isActive: values.isActive,
        productIds: assignedProducts.map(p => p.id),
      };

      console.info('ContractForm: Payload being sent:', payload);

      let response;
      if (contract) {
        console.info('ContractForm: Attempting to update contract:', contract.id, payload);
        response = await api.put(`/api/contracts/${contract.id}`, payload);
        showSuccess('Contract Updated', 'Contract details updated successfully!', 'contractUpdateSuccess');
      } else {
        console.info('ContractForm: Attempting to create new contract:', payload);
        response = await api.post('/api/contracts', payload);
        showSuccess('Contract Created', 'New contract created successfully!', 'contractCreateSuccess');
      }

      console.info('ContractForm: API response:', response.data);
      if (hideLoading && typeof hideLoading === 'function') hideLoading();
      onSuccess();
      onCancel();
    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading();
      setLoading(false);
      console.error('ContractForm: Form submission error:', error.response?.data || error.message, error);
      showError('Submission Failed', error.response?.data?.message || 'Failed to save contract.', 'contractFormSubmitError');
    } finally {
      setLoading(false);
    }
  };

  // --- Functions for Assigning/Removing Products ---

  const handleAssignProduct = async () => {
    console.info('handleAssignProduct: Function triggered.');

    if (!productToAssign) {
      showError('Selection Required', 'Please select a product to assign.', 'assignProductWarning');
      return;
    }

    const productToAdd = allProducts.find(p => p.id === productToAssign);

    if (!productToAdd) {
      showError('Product Not Found', 'Selected product not found in available products list.', 'productNotFoundWarning');
      return;
    }

    const loadingKey = `assignProductLocal-${productToAssign}`;
    const hideLoading = showLoading(loadingKey, 'Adding product to list...');
    try {
      setAssignedProducts(prev => {
        if (prev.some(p => p.id === productToAdd.id)) {
          showError('Duplicate Product', 'Product is already assigned to this contract.', 'duplicateProductWarning');
          return prev;
        }
        const newAssignedList = [...prev, productToAdd];
        showSuccess('Product Added', 'Product added to list. Click Submit to save changes.', 'productAddedToListSuccess');
        return newAssignedList;
      });
    } finally {
      hideLoading();
    }

    setProductToAssign(null);
  };

  const handleRemoveAssignedProduct = (productIdToRemove) => {
    setAssignedProducts(prev => prev.filter(p => p.id !== productIdToRemove));
    showSuccess('Product Removed', 'Product removed from list. Click Submit to save changes.', 'productRemovedFromListSuccess');
  };

  const assignedProductsColumns = [
    {
      title: 'Product Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Product Code',
      dataIndex: 'code',
      key: 'code',
      sorter: (a, b) => (a.code || '').localeCompare(b.code || ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={`Are you sure you want to remove "${record.name}" from this contract?`}
          onConfirm={() => handleRemoveAssignedProduct(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />} aria-label={`Remove ${record.name}`} />
        </Popconfirm>
      ),
    },
  ];

  const availableProductsForAssignment = allProducts.filter(
    (product) => !assignedProducts.some((assigned) => assigned.id === product.id)
  );

  // 🆕 Assign user to contract (immediate API call)
  const handleAssignUser = async () => {
    if (!contract || !contract.id) {
      showError('Contract Required', 'Please save the contract first before assigning users.', 'assignUserNoContract');
      return;
    }
    if (!assignEUCEnabled) {
      showError('EUC Not Enabled', 'Please tick the EUC checkbox first.', 'assignUserNotEUC');
      return;
    }
    if (!userToAssign) {
      showError('Selection Required', 'Please select a user to assign.', 'assignUserNoSelection');
      return;
    }

    const userObj = availableUsers.find(u => u.id === userToAssign);
    if (!userObj) {
      showError('User Not Found', 'Selected user not found.', 'assignUserNotFound');
      return;
    }

    if (assignedUsers.some(u => u.userId === userToAssign)) {
      showError('Duplicate User', 'This user is already assigned to this contract.', 'assignUserDuplicate');
      return;
    }

    setUsersLoading(true);
    try {
      await api.post(`/api/contracts/${contract.id}/users`, { userId: userToAssign });
      showSuccess('User Assigned', `${userObj.name} has been assigned to this contract.`, 'assignUserSuccess');
      setUserToAssign(null);
      await refetchAssignedUsers();
    } catch (error) {
      console.error('Error assigning user to contract:', error.response?.data || error.message);
      showError('Assignment Failed', error.response?.data?.message || 'Failed to assign user.', 'assignUserError');
    } finally {
      setUsersLoading(false);
    }
  };

  // 🆕 Remove user from contract (immediate API call)
  const handleRemoveUser = async (userIdToRemove) => {
    if (!contract || !contract.id) return;

    setUsersLoading(true);
    try {
      await api.patch(`/api/contracts/${contract.id}/users/${userIdToRemove}/remove`);
      showSuccess('User Removed', 'User assignment removed successfully.', 'removeUserSuccess');
      await refetchAssignedUsers();
    } catch (error) {
      console.error('Error removing user from contract:', error.response?.data || error.message);
      showError('Removal Failed', error.response?.data?.message || 'Failed to remove user.', 'removeUserError');
    } finally {
      setUsersLoading(false);
    }
  };

  // 🆕 Column definitions for the assigned users table
  const assignedUsersColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName || ''} ${record.lastName || ''}`.trim() || '—',
      sorter: (a, b) => {
        const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'Role',
      dataIndex: 'roleName',
      key: 'roleName',
      render: (val) => val || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={`Remove this user from the contract?`}
          onConfirm={() => handleRemoveUser(record.userId)}
          okText="Yes"
          cancelText="No"
          disabled={usersLoading}
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            disabled={usersLoading}
            aria-label={`Remove user`}
          />
        </Popconfirm>
      ),
    },
  ];

  // 🆕 Filter available users to exclude already-assigned ones
  const availableUsersForAssignment = availableUsers.filter(
    (u) => !assignedUsers.some((assigned) => assigned.userId === u.id)
  );

  return (
    <Modal
      open={visible}
      title={contract ? 'Edit Contract' : 'Add New Contract'}
      onCancel={onCancel}
      footer={[
        <Button
          key="manage-sla"
          onClick={() => setIsSlaModalVisible(true)}
          disabled={!contract || loading}
        >
          Manage Custom SLAs
        </Button>,
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={900}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        name="contract_form"
        initialValues={{
          ladPenaltyRate: 0,
          maintenancePeriodUnit: 'Yearly',
          agreedResponseTimeValue: 1,
          agreedResponseTimeUnit: 'hour',
          agreedResolutionTimeValue: 4,
          agreedResolutionTimeUnit: 'hour',
          isActive: true,
          mainContractorOrganizationId: 1,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contractName"
              label="Contract Name"
              rules={[
                { required: true, message: 'Please input the contract name!' },
                { type: 'string', min: 2, message: 'Contract name must be at least 2 characters.' }
              ]}
            >
              <Input placeholder="e.g., Enterprise Support Agreement" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="reportTitle"
              label="Report Title"
              rules={[{ required: true, message: 'Please input the report title!' }]}
            >
              <Input placeholder="e.g., Quarterly Performance Report" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="organizationId"
              label="Customer Organization"
              rules={[{ required: true, message: 'Please select a customer organization!' }]}
              tooltip="The customer who receives the service (e.g., LHDNM)"
            >
              <Select
                showSearch
                placeholder="Select a customer organization"
                loading={!allOrganizations.length}
                filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {allOrganizations.map(org => (
                  <Option key={org.id} value={org.id}>
                    {org.name} ({org.type || 'N/A'})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="mainContractorOrganizationId"
              label="Main Contractor"
              rules={[{ required: true, message: 'Please select the main contractor!' }]}
              tooltip="The organization responsible for delivering the service (YPS or Partner)"
            >
              <Select
                showSearch
                placeholder="Select main contractor (YPS or Partner)"
                loading={!allMainContractorOrgs.length}
                filterOption={(input, option) =>
                  option.children && option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {allMainContractorOrgs.map(org => (
                  <Option key={org.id} value={org.id}>
                    {org.name} ({org.type === 'internal' ? 'Internal - YPS' : 'Partner'})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="startDate"
              label="Contract Start Date"
              rules={[{ required: true, message: 'Please select a start date!' }]}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="endDate"
              label="Contract End Date"
              rules={[
                { required: true, message: 'Please select an end date!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue('startDate');
                    if (!value || !startDate) {
                      return Promise.resolve();
                    }
                    if (value.isSameOrAfter(startDate, 'day')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('End date must be on or after start date!'));
                  },
                }),
              ]}
            >
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ladPenaltyRate"
              label="LAD Penalty Rate"
              rules={[
                { required: true, message: 'Please input the LAD penalty rate!' },
                { type: 'number', transform: Number, min: 0, message: 'Rate must be a non-negative number.' }
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step="0.01" placeholder="e.g., 12.66" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="maintenancePeriodUnit"
              label="Maintenance Period Unit"
              rules={[{ required: true, message: 'Please select a maintenance period unit!' }]}
            >
              <Select placeholder="Select unit">
                <Option value="Monthly">Monthly</Option>
                <Option value="Half Yearly">Half Yearly</Option>
                <Option value="Quarterly">Quarterly</Option>
                <Option value="Yearly">Yearly</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="form-section-header" style={{ marginTop: '30px', marginBottom: '20px' }}>
          <Divider orientation="left">SLA Definitions</Divider>
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="agreedResponseTimeValue"
              label="Agreed Response Time Value"
              rules={[
                { required: true, message: 'Please input the response time value!' },
                { type: 'number', transform: Number, min: 0, message: 'Value must be a non-negative number.' }
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} placeholder="e.g., 2" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="agreedResponseTimeUnit"
              label="Agreed Response Time Unit"
              rules={[{ required: true, message: 'Please select a response time unit!' }]}
            >
              <Select placeholder="Select unit">
                <Option value="hour">Hour(s)</Option>
                <Option value="day">Day(s)</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="agreedResolutionTimeValue"
              label="Agreed Resolution Time Value"
              rules={[
                { required: true, message: 'Please input the resolution time value!' },
                { type: 'number', transform: Number, min: 0, message: 'Value must be a non-negative number.' }
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={0} placeholder="e.g., 24" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="agreedResolutionTimeUnit"
              label="Agreed Resolution Time Unit"
              rules={[{ required: true, message: 'Please select a resolution time unit!' }]}
            >
              <Select placeholder="Select unit">
                <Option value="hour">Hour(s)</Option>
                <Option value="day">Day(s)</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="isActive"
          valuePropName="checked"
          style={{ marginTop: '20px' }}
        >
          <Checkbox>Active</Checkbox>
        </Form.Item>

        <div className="form-section-header" style={{ marginTop: '30px', marginBottom: '20px' }}>
          <Divider orientation="left">Assigned Products</Divider>
        </div>

        <div className="form-subsection">
          <h3 className="form-subsection-heading">Add Products to Contract</h3>
          <Row gutter={16} align="middle">
            <Col span={16}>
              <Form.Item noStyle>
                <Select
                  showSearch
                  placeholder="Select product to assign (search by name or code)"
                  style={{ width: '100%' }}
                  value={productToAssign}
                  onChange={setProductToAssign}
                  loading={assigningLoading || !allProducts.length}
                  disabled={assigningLoading || availableProductsForAssignment.length === 0}
                  filterOption={(input, option) =>
                    option.children && option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {availableProductsForAssignment.map(product => (
                    <Option key={product.id} value={product.id}>
                      {product.name} ({product.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAssignProduct}
                loading={assigningLoading}
                disabled={!productToAssign}
              >
                Add Product
              </Button>
            </Col>
          </Row>
        </div>

        <div className="form-subsection">
          <h3 className="form-subsection-heading">Products on This Contract</h3>
          <Table
            columns={assignedProductsColumns}
            dataSource={assignedProducts.sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
            rowKey="id"
            pagination={false}
            loading={assigningLoading}
            size="small"
            locale={{ emptyText: 'No products assigned to this contract yet.' }}
          />
        </div>

        {/* 🆕 EUC User Assignment Section */}
        <div className="form-section-header" style={{ marginTop: '30px', marginBottom: '20px' }}>
          <Divider orientation="left">EUC User Assignment</Divider>
        </div>

        <div className="form-subsection">
          <Form.Item noStyle>
            <Checkbox
              checked={assignEUCEnabled}
              onChange={(e) => setAssignEUCEnabled(e.target.checked)}
              disabled={!contract}
            >
              Please check/tick if this is an EUC contract
            </Checkbox>
          </Form.Item>

          {!contract && (
            <p style={{ color: '#888', marginTop: 8, marginBottom: 16 }}>
              Save the contract first to assign users.
            </p>
          )}

          {contract && assignEUCEnabled && assignedUsers.length === 0 && (
            <p style={{ color: '#fa8c16', marginTop: 8, marginBottom: 16, fontSize: 13 }}>
              ⓘ If this contract doesn't yet have an EUC product linked, please add one above, click <strong>Submit</strong> to save, then reopen the contract to assign users.
            </p>
          )}

          <h3 className="form-subsection-heading" style={{ marginTop: '16px' }}>
            Assign EUC user(s) to contract
          </h3>

          <Row gutter={16} align="middle" style={{ marginTop: 8 }}>
            <Col span={16}>
              <Form.Item noStyle>
                <Select
                  showSearch
                  placeholder={
                    !contract
                      ? 'Save contract first...'
                      : !assignEUCEnabled
                      ? 'Tick the EUC checkbox above to enable...'
                      : 'Select user to assign (search by name or email)'
                  }
                  style={{ width: '100%' }}
                  value={userToAssign}
                  onChange={setUserToAssign}
                  loading={usersLoading || !availableUsers.length}
                  disabled={!contract || !assignEUCEnabled || usersLoading || availableUsersForAssignment.length === 0}
                  filterOption={(input, option) =>
                    option.children && option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {availableUsersForAssignment.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Button
                type="primary"
                icon={<UserOutlined />}
                onClick={handleAssignUser}
                loading={usersLoading}
                disabled={!contract || !assignEUCEnabled || !userToAssign}
              >
                Add User
              </Button>
            </Col>
          </Row>
        </div>

        <div className="form-subsection">
          <h3 className="form-subsection-heading">Users on This Contract</h3>
          <Table
            columns={assignedUsersColumns}
            dataSource={assignedUsers}
            rowKey="userId"
            pagination={false}
            loading={usersLoading}
            size="small"
            locale={{ emptyText: 'No users assigned to this contract yet.' }}
          />
        </div>
      </Form>

      <ContractSlaManagementModal
        visible={isSlaModalVisible}
        onCancel={() => setIsSlaModalVisible(false)}
        contractId={contract?.id}
        contractName={contract?.contractName}
      />
    </Modal>
  );
};

export default ContractForm;